/**
 * Who may use an authorization (ADR-0329).
 *
 * A personal OAuth connection (Google, Meta, Microsoft Ads) is usable only by
 * the person who connected it. The backend says so per row with
 * `usable_by_me`; the pickers here show such rows DISABLED with a hint rather
 * than hiding them, so a colleague understands why the connection they can see
 * on the Authorizations page is not offered and connects their own account.
 *
 * Only an explicit `usable_by_me === false` disables a row: an absent field
 * (older response, shared connection type) means usable.
 */
import type { ComboboxItem, SelectProps } from "@mantine/core";
import { CheckIcon, Stack, Text } from "@mantine/core";

import type { AuthorizationDTO } from "@/types/authorizations";

type OwnershipFields = Pick<
  AuthorizationDTO,
  "is_personal" | "usable_by_me" | "created_by_name" | "owner_is_member"
>;

export function isUsableByMe(a: OwnershipFields): boolean {
  return a.usable_by_me !== false;
}

/** "Connected by" cell on the Authorizations page. */
export function connectedByLabel(a: OwnershipFields): string {
  if (a.is_personal && a.owner_is_member === false) return "Removed user";
  return a.created_by_name?.trim() || "—";
}

/** Why a personal row is disabled in a picker; null when it is usable. */
export function unusableHint(a: OwnershipFields): string | null {
  if (isUsableByMe(a)) return null;
  const name = a.created_by_name?.trim();
  if (a.owner_is_member === false || !name) {
    return "Connected by a removed user. Connect your own account to use it.";
  }
  return `Connected by ${name}. Connect your own account to use it.`;
}

/**
 * Select options for a list of authorizations. A row not usable by me is
 * disabled, except `keepSelectable` (the authorization a source currently runs
 * on: it must stay displayed as the current value even when it is not mine).
 */
export function authorizationSelectData(
  auths: AuthorizationDTO[],
  opts?: { label?: (a: AuthorizationDTO) => string; keepSelectable?: string | null }
): ComboboxItem[] {
  return auths.map((a) => ({
    value: a.id,
    label: opts?.label ? opts.label(a) : a.name,
    disabled: !isUsableByMe(a) && a.id !== opts?.keepSelectable,
  }));
}

/** The first authorization I may use, for a picker's default; null if none. */
export function firstUsable(auths: AuthorizationDTO[]): AuthorizationDTO | null {
  return auths.find(isUsableByMe) ?? null;
}

/**
 * `renderOption` for a Select built with `authorizationSelectData`: renders the
 * hint under a disabled personal row. Mantine's default body (with its check
 * icon) is replaced, so the check is re-rendered for the selected row.
 */
export function authorizationRenderOption(
  auths: AuthorizationDTO[]
): NonNullable<SelectProps["renderOption"]> {
  const byId = new Map(auths.map((a) => [a.id, a]));
  return ({ option, checked }) => {
    const a = byId.get(option.value);
    const hint = a ? unusableHint(a) : null;
    return (
      <Stack gap={0} style={{ flex: 1 }}>
        <Text size="sm" span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {checked && <CheckIcon size={12} />}
          {option.label}
        </Text>
        {hint && (
          <Text size="xs" c="dimmed" span>
            {hint}
          </Text>
        )}
      </Stack>
    );
  };
}
